import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { ConsultationConversion } from './ConsultationConversion';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = 'h-9 border border-neutral-300 bg-white px-2 text-sm rounded';

const Stat = ({ label, value, sub, id }) => (
  <div className="bg-white border border-neutral-200 rounded-lg px-4 py-3 min-w-0">
    <p className="text-xs text-neutral-500">{label}</p>
    <p className="text-lg font-semibold text-neutral-900 truncate" title={value} data-testid={`consult-summary-${id}`}>{value}</p>
    {sub && <p className="text-xs text-neutral-400">{sub}</p>}
  </div>
);

// Source + submission-date (New York time) filters with a summary of where consultation requests came from.
export const ConsultationSourceFilter = ({ token, filters, onChange, refreshKey }) => {
  const [data, setData] = useState(null);
  const { source = '', date_from = '', date_to = '' } = filters;
  useEffect(() => {
    axios.get(`${API_URL}/admin/consultations/sources`, { headers: { Authorization: `Bearer ${token}` }, params: { date_from: date_from || undefined, date_to: date_to || undefined } })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [token, date_from, date_to, refreshKey]);
  const set = (k, v) => onChange({ ...filters, [k]: v });
  const exportConversion = async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/consultations/conversion/export`, { headers: { Authorization: `Bearer ${token}` }, params: { date_from: date_from || undefined, date_to: date_to || undefined }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = Object.assign(document.createElement('a'), { href: url, download: `consultation_conversion_${date_from || 'all'}_${date_to || 'now'}.csv` });
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Conversion report downloaded');
    } catch { toast.error('Failed to export conversion report'); }
  };
  const active = source || date_from || date_to;
  return (
    <div className="mb-5 space-y-3" data-testid="consult-source-filter">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <Stat id="total" label="Total consultations" value={data?.total ?? '—'} />
        <Stat id="top-source" label="Top source page" value={data?.top_source?.title || '—'} sub={data?.top_source && `${data.top_source.count} request${data.top_source.count === 1 ? '' : 's'}`} />
        <Stat id="top-cta" label="Top CTA / button" value={data?.top_cta?.cta || '—'} sub={data?.top_cta && `${data.top_cta.count} request${data.top_cta.count === 1 ? '' : 's'}`} />
        <Stat id="direct" label="Direct visits" value={data?.direct ?? '—'} sub={data ? `${data.not_recorded} not recorded` : null} />
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-neutral-500">Source page
          <select className={`${field} block mt-1 min-w-[16rem]`} value={source} onChange={(e) => set('source', e.target.value)} data-testid="consult-filter-source">
            <option value="">All sources</option>
            {source && !(data?.sources || []).some((s) => s.key === source) && <option value={source}>{source} — 0</option>}
            {(data?.sources || []).map((s) => <option key={s.key} value={s.key}>{s.title}{s.path ? ` (${s.path})` : ''} — {s.count}</option>)}
          </select>
        </label>
        <label className="text-xs text-neutral-500">From
          <input type="date" className={`${field} block mt-1`} value={date_from} max={date_to || undefined} onChange={(e) => set('date_from', e.target.value)} data-testid="consult-filter-from" />
        </label>
        <label className="text-xs text-neutral-500">To
          <input type="date" className={`${field} block mt-1`} value={date_to} min={date_from || undefined} onChange={(e) => set('date_to', e.target.value)} data-testid="consult-filter-to" />
        </label>
        {active && <button type="button" onClick={() => onChange({})} className="h-9 px-3 text-sm text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1" data-testid="consult-filter-clear"><X className="w-4 h-4" />Clear</button>}
        <span className="text-xs text-neutral-400 pb-2">Dates use submission time, New York.</span>
      </div>
      <ConsultationConversion sources={data?.sources} onExport={exportConversion} />
    </div>
  );
};
